import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { resolveUrl, getSpecRows } from "./api";

const BLUE = [31, 111, 196];
const SLATE = [15, 23, 42];
const GOLD = [201, 162, 39];

function header(doc, W, label) {
  doc.setFillColor(...SLATE);
  doc.rect(0, 0, W, 90, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 88, W, 5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text("HMA", 40, 44);
  doc.setTextColor(...BLUE);
  doc.setFontSize(10);
  doc.text("CONSTRUCTION CHEMICALS", 40, 60);
  doc.setTextColor(200, 210, 220);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(label, W - 40, 44, { align: "right" });
  doc.text(new Date().toLocaleDateString("en-GB"), W - 40, 59, { align: "right" });
}

function footer(doc, W, note) {
  const y = doc.internal.pageSize.getHeight() - 40;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(2);
  doc.line(40, y - 15, W - 40, y - 15);
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(note, 40, y);
}

export function generateDatasheet(product) {
  if (product.datasheet_url) {
    window.open(resolveUrl(product.datasheet_url), "_blank", "noopener,noreferrer");
    return;
  }
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  header(doc, W, "TECHNICAL DATA SHEET");

  doc.setTextColor(...SLATE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(product.name, 40, 140);
  doc.setFontSize(11);
  doc.setTextColor(...BLUE);
  doc.text(`${product.code}  |  ${product.category}`, 40, 160);

  doc.setTextColor(60, 60, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const desc = doc.splitTextToSize(product.description || "", W - 80);
  doc.text(desc, 40, 185);
  const startY = 185 + desc.length * 13 + 15;

  autoTable(doc, {
    startY,
    head: [["Property", "Specification"]],
    body: getSpecRows(product).map(([label, value]) => [label, value || "-"]),
    theme: "grid",
    headStyles: { fillColor: SLATE, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 200, textColor: SLATE } },
    styles: { fontSize: 10, cellPadding: 8 },
    margin: { left: 40, right: 40 },
  });

  footer(doc, W, "HMA Construction Chemicals  |  Values are typical and subject to normal manufacturing tolerances.");
  doc.save(`${product.code || product.name}-TDS.pdf`);
}

export function generateMSDS(product) {
  if (product.msds_url) {
    window.open(resolveUrl(product.msds_url), "_blank", "noopener,noreferrer");
    return;
  }
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  header(doc, W, "MATERIAL SAFETY DATA SHEET");

  doc.setTextColor(...SLATE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(`MSDS: ${product.name}`, 40, 138);
  doc.setFontSize(11);
  doc.setTextColor(...BLUE);
  doc.text(`${product.code}  |  ${product.category}`, 40, 158);

  const sections = [
    ["1. Identification", `Product: ${product.name} (${product.code})\nProduct type: Concrete admixture — ${product.category}\nManufacturer: HMA Construction Chemicals\nStandard: ${product.standard || "N/A"}`],
    ["2. Hazards Identification", "Non-hazardous under normal conditions of use. May cause mild irritation on prolonged skin or eye contact. Keep out of reach of children."],
    ["3. Physical & Chemical Properties", `Appearance: ${product.appearance || "N/A"}\nSpecific Gravity @ 25\u00B0C: ${product.specific_gravity || "N/A"}\npH Value: ${product.ph_value || "N/A"}\nChloride Content: ${product.chloride_content || "N/A"}`],
    ["4. First Aid Measures", "Eyes: Rinse with plenty of clean water for 15 minutes; seek medical advice if irritation persists.\nSkin: Wash with soap and water.\nIngestion: Do not induce vomiting; rinse mouth and seek medical attention.\nInhalation: Move to fresh air."],
    ["5. Handling & Storage", "Store in original sealed containers in a cool, dry, well-ventilated area away from direct sunlight and frost. Protect from freezing. Wear gloves and eye protection during handling."],
    ["6. Disposal Considerations", "Dispose of contents and container in accordance with local, regional and national regulations. Do not discharge into drains or watercourses."],
  ];

  let y = 185;
  sections.forEach(([title, body]) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...SLATE);
    doc.text(title, 40, y);
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(70, 70, 70);
    const lines = doc.splitTextToSize(body, W - 80);
    doc.text(lines, 40, y);
    y += lines.length * 12 + 14;
  });

  footer(doc, W, "HMA Construction Chemicals  |  Safety information provided in good faith. Users must assess suitability for their conditions.");
  doc.save(`${product.code || product.name}-MSDS.pdf`);
}
